import React, { useState, useEffect, useRef } from 'react';
import {
    AccountBalanceWallet,
    Speed,
    Schedule,
    Security,
    TrendingUp,
    Download,
    FilterList,
    Add,
    QrCode,
    Close,
    CalendarToday,
    Payment,
    AccountBalance,
    Receipt,
    CheckCircle
} from '@mui/icons-material';
import AdminSidebar from "../../components/admin/AdminSidebar";
import DashboardHeader from "../../components/admin/DashboardHeader";
import WithdrawalRequestForm from "../../components/withdrawal/WithdrawalRequestForm";
import ConfirmWithdrawalModal from "../../components/withdrawal/ConfirmWithdrawalModal";
import WithdrawPolicySection from "../../components/withdrawal/WithdrawPolicySection";
import { useAuth } from "../../contexts/AuthContext";
import { fetchCompanyPos } from "../../services/posService";
import { fetchTransactions, formatDateForAPI, displayReference } from "../../services/transactionService";
import TransactionModal from "../../components/admin/transactions/TransactionModal";
import { storageKeys } from "../../config/environment";
import { mapTransactionStatus } from "../../utils/transactionCategory";
import { resolveOperator } from "../../utils/operator";

const WithdrawalPage = () => {
    const { selectedOrganization } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    const [activeTab, setActiveTab] = useState('manual');
    const [showNewWithdrawalModal, setShowNewWithdrawalModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmData, setConfirmData] = useState(null);
    const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
    const [withdrawalMode, setWithdrawalMode] = useState('manual');
    const [showMobileMoneyModal, setShowMobileMoneyModal] = useState(false);
    const [selectedPos, setSelectedPos] = useState(null);
    const [posList, setPosList] = useState([]);

    useEffect(() => {
        if (selectedOrganization?.id) {
            loadPosList();
        }
    }, [selectedOrganization]);

    const loadPosList = async () => {
        try {
            const list = await fetchCompanyPos(selectedOrganization.id);
            setPosList(list);
            if (list.length > 0 && !selectedPos) {
                setSelectedPos(list[0]);
            }
        } catch (error) {
            console.error('Error loading POS list:', error);
        }
    };

    const handleWithdrawalSuccess = (data) => {
        // Ouvrir le modal de confirmation avec les données reçues
        setConfirmData({
            expires_in: data.expires_in,
            trans_id: data.trans_id
        });
        setShowNewWithdrawalModal(false);
        setShowConfirmModal(true);
    };

    const handleConfirmSuccess = () => {
        setShowConfirmModal(false);
        setConfirmData(null);
        // Recharger l'historique
        loadWithdrawals();
    };
    const [withdrawalData, setWithdrawalData] = useState({
        amount: '',
        method: '',
        methodType: '', // 'bank' ou 'mobile'
        bankProvider: '',
        mobileMoneyProvider: '',
        accountNumber: '',
        phoneNumber: '',
        description: ''
    });

    const [withdrawalHistory, setWithdrawalHistory] = useState([]);
    const [withdrawalStats, setWithdrawalStats] = useState({
        total: 0,
        pending: 0,
        completed: 0,
        failed: 0
    });
    const [loadingWithdrawals, setLoadingWithdrawals] = useState(true);
    const [currencyLoading, setCurrencyLoading] = useState(false);
    const hasLoadedOnceRef = useRef(false);

    useEffect(() => {
        if (selectedOrganization?.id) {
            loadWithdrawals({ silent: hasLoadedOnceRef.current });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrganization, timeRange, currency]);

    const loadWithdrawals = async ({ silent = false } = {}) => {
        if (!selectedOrganization) return;
        
        try {
            // Refresh silencieux lors d'un changement de devise/période
            const shouldSilent = silent || hasLoadedOnceRef.current;
            if (shouldSilent) {
                setCurrencyLoading(true);
            } else {
                setLoadingWithdrawals(true);
            }
            const solutionId = localStorage.getItem('netikash_solution') || 'all';

            // Calculer les dates
            const now = new Date();
            let startDate = new Date();
            
            if (timeRange === 'today') {
                startDate.setHours(0, 0, 0, 0);
            } else if (timeRange === 'week') {
                startDate.setDate(now.getDate() - 7);
            } else if (timeRange === 'month') {
                startDate.setMonth(now.getMonth() - 1);
            }

            // Charger les transactions de type withdrawal (backend attend le code opération "WDL")
            const response = await fetchTransactions({
                own: selectedOrganization.id,
                sol: solutionId,
                crc: currency,
                opr: 'WDL',
                sts: 'all',
                fdt: formatDateForAPI(startDate),
                tdt: formatDateForAPI(now),
                lmt: 1000,
                pge: 1
            });

            const transactions = response.contents || [];
            
            // Transformer en historique de retraits
            const withdrawals = transactions.map(t => {
                const transactionDate = t.state?.requested_at || t.createdAt || new Date();
                const dateObj = new Date(transactionDate);
                
                return {
                    id: t.id,
                    // Même référence que la liste des transactions et le reçu
                    withdrawalNumber: displayReference(t),
                    reference: displayReference(t),
                    type: 'withdrawal',
                    typeLabel: 'Retrait',
                    raw: t,
                    amount: t.netAmount || t.amount || 0,
                    currency: t.currency || 'CDF',
                    // TRS : requested / approved = en attente (pas "failed")
                    status: mapTransactionStatus(t.status),
                    method: t.provider_name || 'Mobile Money',
                    provider: resolveOperator({ name: t.provider_name, providerId: t.provider_id }) || '—',
                    date: dateObj.toLocaleDateString('fr-FR'),
                    time: dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
                    account: t.client_name || '—',
                    fees: t.fees || 0,
                    netAmount: t.netAmount || t.amount || 0,
                    transactionId: displayReference(t)
                };
            });

            // Calculer les statistiques
            const completed = withdrawals.filter(w => w.status === 'completed');
            const pending = withdrawals.filter(w => w.status === 'pending');
            const failed = withdrawals.filter(w => w.status === 'failed');

            setWithdrawalHistory(withdrawals);
            setWithdrawalStats({
                total: withdrawals.length,
                pending: pending.length,
                completed: completed.length,
                failed: failed.length
            });
        } catch (error) {
            console.error('Error loading withdrawals:', error);
            setWithdrawalHistory([]);
        } finally {
            setLoadingWithdrawals(false);
            setCurrencyLoading(false);
            hasLoadedOnceRef.current = true;
        }
    };

    // Historique des retraits (mock pour référence)
    const mockWithdrawalHistory = [
        {
            id: 1,
            withdrawalNumber: '+243 81 123 4567',
            amount: 1500,
            currency: 'USD',
            status: 'completed',
            method: 'Virement Bancaire',
            provider: 'Trust Merchant Bank',
            date: '2024-01-15',
            time: '14:30',
            account: '***1234',
            fees: 0,
            netAmount: 1500,
            transactionId: 'TX123456789'
        },
        {
            id: 2,
            withdrawalNumber: '+243 97 765 4321',
            amount: 500,
            currency: 'USD',
            status: 'completed',
            method: 'Mobile Money',
            provider: 'Airtel Money',
            date: '2024-01-14',
            time: '09:15',
            account: '+243 97 765 4321',
            fees: 7.5,
            netAmount: 492.5,
            transactionId: 'TX987654321'
        }
    ];

    const withdrawalOptions = [
        {
            id: 'manual',
            title: 'Retrait Manuel',
            icon: AccountBalanceWallet,
            description: 'Retraits à la demande',
            features: ['Gratuit', '1-2 jours', 'Mobile Money/Banque'],
            color: 'from-green-500 to-emerald-500',
            fee: '0%'
        },
        {
            id: 'instant',
            title: 'Retrait Instantané',
            icon: Speed,
            description: 'Accès immédiat à vos fonds',
            features: ['Instantané', 'Mobile Money', 'KYC requis'],
            color: 'from-purple-500 to-pink-500',
            fee: '1.5%'
        }
    ];

    // Options de Mobile Money
    const mobileMoneyProviders = [
        {
            id: 'airtel',
            name: 'Airtel Money',
            color: 'bg-red-500',
            icon: '🔴',
            description: 'Service mobile money Airtel'
        },
        {
            id: 'orange',
            name: 'Orange Money',
            color: 'bg-orange-500',
            icon: '🟠',
            description: 'Service mobile money Orange'
        },
        {
            id: 'mpesa',
            name: 'M-Pesa',
            color: 'bg-teal-500',
            icon: '🟢',
            description: 'Service mobile money M-Pesa'
        },
        {
            id: 'afrimoney',
            name: 'AfriMoney',
            color: 'bg-blue-500',
            icon: '🔵',
            description: 'Service mobile money AfriMoney'
        }
    ];

    // Banques disponibles
    const bankProviders = [
        { id: 'rawbank', name: 'Rawbank', code: 'RAW' },
        { id: 'trust', name: 'Trust Merchant Bank', code: 'TMB' },
        { id: 'bcdc', name: 'BCDC', code: 'BCD' },
        { id: 'ecobank', name: 'Ecobank', code: 'ECO' },
        { id: 'stanbic', name: 'Stanbic Bank', code: 'STA' }
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-800 border border-green-200';
            case 'pending': return 'bg-amber-100 text-amber-800 border border-amber-200';
            case 'failed': return 'bg-red-100 text-red-800 border border-red-200';
            default: return 'bg-gray-100 text-gray-800 border border-gray-200';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'completed': return 'Complété';
            case 'pending': return 'En attente';
            case 'failed': return 'Échoué';
            default: return 'Inconnu';
        }
    };

    const getMethodIcon = (method) => {
        switch (method) {
            case 'Virement Bancaire': return <AccountBalance className="text-blue-600" />;
            case 'Mobile Money': return <Payment className="text-green-600" />;
            default: return <Receipt className="text-gray-600" />;
        }
    };

    const getMethodColor = (method) => {
        switch (method) {
            case 'Virement Bancaire': return 'bg-blue-100';
            case 'Mobile Money': return 'bg-green-100';
            default: return 'bg-gray-100';
        }
    };

    const handleNewWithdrawal = () => {
        setShowNewWithdrawalModal(true);
    };

    const handleCloseModal = () => {
        setShowNewWithdrawalModal(false);
        setShowMobileMoneyModal(false);
        setSelectedWithdrawal(null);
        setWithdrawalData({
            amount: '',
            method: '',
            methodType: '',
            bankProvider: '',
            mobileMoneyProvider: '',
            accountNumber: '',
            phoneNumber: '',
            description: ''
        });
    };

    const handleWithdrawalClick = (withdrawal) => {
        setSelectedWithdrawal(withdrawal);
    };

    const handleInputChange = (field, value) => {
        setWithdrawalData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleMethodTypeChange = (type) => {
        setWithdrawalData(prev => ({
            ...prev,
            methodType: type,
            method: type === 'bank' ? 'Virement Bancaire' : 'Mobile Money',
            bankProvider: '',
            mobileMoneyProvider: '',
            accountNumber: '',
            phoneNumber: ''
        }));
    };

    const handleMobileMoneyProviderSelect = (provider) => {
        setWithdrawalData(prev => ({
            ...prev,
            mobileMoneyProvider: provider.id,
            method: `Mobile Money - ${provider.name}`
        }));
        setShowMobileMoneyModal(false);
    };

    const handleSubmitWithdrawal = () => {
        console.log('Nouveau retrait:', withdrawalData);
        alert('Demande de retrait soumise avec succès !');
        handleCloseModal();
    };

    const calculateFee = () => {
        if (activeTab === 'instant' && withdrawalData.amount) {
            return (parseFloat(withdrawalData.amount) * 0.015).toFixed(2);
        }
        return 0;
    };

    const calculateTotal = () => {
        if (withdrawalData.amount) {
            const amount = parseFloat(withdrawalData.amount);
            const fee = activeTab === 'instant' ? amount * 0.015 : 0;
            return (amount - fee).toFixed(2);
        }
        return 0;
    };

    const formatDate = (dateString) => {
        const options = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleDateString('fr-FR', options);
    };

    const toggleWithdrawalMode = () => {
        setWithdrawalMode(prev => prev === 'manual' ? 'instant' : 'manual');
    };

    return (
        <div className="min-h-screen bg-slate-50 flex">
            <AdminSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="withdrawals"
            />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="min-h-screen p-4 lg:p-6">
                    <div className="space-y-6">
                        <DashboardHeader
                            timeRange={timeRange}
                            onTimeRangeChange={setTimeRange}
                            currency={currency}
                            onCurrencyChange={setCurrency}
                            currencyLoading={currencyLoading}
                            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                        />

                        {/* En-tête */}
                        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                            <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent px-6 py-5">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                                            <AccountBalanceWallet className="text-white text-2xl" />
                                        </div>
                                        <div>
                                            <h1 className="text-2xl font-bold text-slate-800">Retraits</h1>
                                            <p className="text-slate-600 text-sm mt-0.5">Demandez et suivez vos retraits de fonds</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={toggleWithdrawalMode}
                                            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                                withdrawalMode === 'manual'
                                                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                                    : 'bg-purple-100 text-purple-700 border border-purple-200'
                                            }`}
                                        >
                                            {withdrawalMode === 'manual' ? <AccountBalanceWallet className="text-lg" /> : <Speed className="text-lg" />}
                                            {withdrawalMode === 'manual' ? 'Manuel' : 'Instantané'}
                                        </button>
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 text-sm font-medium"
                                        >
                                            <Download fontSize="small" />
                                            Exporter
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleNewWithdrawal}
                                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-all shadow-md hover:shadow-lg"
                                        >
                                            <Add className="text-lg" />
                                            Nouveau retrait
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Statistiques */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total</p>
                                        <p className="text-2xl font-bold text-slate-900 mt-1">{withdrawalStats.total}</p>
                                        <p className="text-xs text-slate-500 mt-0.5">retraits</p>
                                    </div>
                                    <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center">
                                        <TrendingUp className="text-amber-600" />
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">En attente</p>
                                        <p className="text-2xl font-bold text-amber-600 mt-1">{withdrawalStats.pending}</p>
                                        <p className="text-xs text-slate-500 mt-0.5">retraits</p>
                                    </div>
                                    <div className="w-11 h-11 rounded-xl bg-amber-100/80 flex items-center justify-center">
                                        <Schedule className="text-amber-600" />
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Complétés</p>
                                        <p className="text-2xl font-bold text-emerald-600 mt-1">{withdrawalStats.completed}</p>
                                        <p className="text-xs text-slate-500 mt-0.5">retraits</p>
                                    </div>
                                    <div className="w-11 h-11 rounded-xl bg-emerald-100/80 flex items-center justify-center">
                                        <CheckCircle className="text-emerald-600" />
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Échoués</p>
                                        <p className="text-2xl font-bold text-red-600 mt-1">{withdrawalStats.failed}</p>
                                        <p className="text-xs text-slate-500 mt-0.5">retraits</p>
                                    </div>
                                    <div className="w-11 h-11 rounded-xl bg-red-100/80 flex items-center justify-center">
                                        <Receipt className="text-red-600" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Politique de retrait (formule, périodicité, compte de destination) */}
                        <WithdrawPolicySection
                            organizationId={selectedOrganization?.id}
                            solutionId={localStorage.getItem(storageKeys.solution) || localStorage.getItem('netikash_solution')}
                            currency={currency}
                        />

                        {/* Historique des retraits */}
                        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
                                <h3 className="text-lg font-semibold text-slate-800">Historique des retraits</h3>
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 text-sm font-medium"
                                >
                                    <FilterList fontSize="small" />
                                    Filtrer
                                </button>
                            </div>

                            <div className="min-h-[280px]">
                                {loadingWithdrawals && withdrawalHistory.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-16">
                                        <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-3" />
                                        <p className="text-slate-500 text-sm">Chargement des retraits...</p>
                                    </div>
                                ) : withdrawalHistory.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-16 px-4">
                                        <div className="w-16 h-16 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center mb-4">
                                            <AccountBalanceWallet className="text-slate-400 text-4xl" />
                                        </div>
                                        <p className="text-slate-800 font-semibold mb-1">Aucun retrait</p>
                                        <p className="text-slate-500 text-sm text-center max-w-sm mb-4">Les retraits apparaîtront ici une fois effectués.</p>
                                        <button
                                            type="button"
                                            onClick={handleNewWithdrawal}
                                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600"
                                        >
                                            <Add className="text-lg" />
                                            Nouveau retrait
                                        </button>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-slate-100">
                                        {withdrawalHistory.map((withdrawal) => (
                                            <div
                                                key={withdrawal.id}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleWithdrawalClick(withdrawal)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleWithdrawalClick(withdrawal)}
                                                className="flex flex-wrap items-center gap-4 px-5 py-4 hover:bg-slate-50/80 transition-colors cursor-pointer"
                                            >
                                                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${getMethodColor(withdrawal.method)}`}>
                                                    {getMethodIcon(withdrawal.method)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                                        <span className="font-semibold text-slate-900">{withdrawal.withdrawalNumber}</span>
                                                        <span className={`inline-flex px-2.5 py-0.5 rounded-lg text-xs font-medium border ${getStatusColor(withdrawal.status)}`}>
                                                            {getStatusText(withdrawal.status)}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-0 text-sm text-slate-500">
                                                        <span className="inline-flex items-center gap-1">
                                                            <CalendarToday className="text-xs" />
                                                            {withdrawal.date} · {withdrawal.time}
                                                        </span>
                                                        <span>{withdrawal.method}</span>
                                                        <span>{withdrawal.provider}</span>
                                                    </div>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <p className="font-bold text-slate-900">
                                                        {Number(withdrawal.amount).toLocaleString('fr-FR')} {withdrawal.currency}
                                                    </p>
                                                    <p className="text-xs text-slate-500">
                                                        Net : {Number(withdrawal.netAmount).toLocaleString('fr-FR')} {withdrawal.currency}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Nouveau Retrait */}
            {showNewWithdrawalModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="withdrawal-modal-title"
                >
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[min(90vh,720px)] overflow-hidden flex flex-col border border-slate-200/80 ring-1 ring-black/5">
                        <div className="relative shrink-0 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-amber-500 to-orange-500" />
                            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
                            <div className="absolute -left-4 bottom-0 w-24 h-24 rounded-full bg-white/5" />
                            <div className="relative flex items-start justify-between gap-4 px-6 py-5">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                                        <AccountBalanceWallet className="text-white text-2xl" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3
                                            id="withdrawal-modal-title"
                                            className="text-lg font-bold text-white tracking-tight"
                                        >
                                            Nouvelle demande de retrait
                                        </h3>
                                        <p className="text-sm text-amber-50/90 mt-0.5">
                                            Renseignez le montant et le compte de destination
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowNewWithdrawalModal(false)}
                                    className="p-2 rounded-xl text-white/90 hover:text-white hover:bg-white/15 transition-colors shrink-0"
                                    aria-label="Fermer"
                                >
                                    <Close />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-6 bg-slate-50/50">
                            <WithdrawalRequestForm
                                onSuccess={handleWithdrawalSuccess}
                                onCancel={() => setShowNewWithdrawalModal(false)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmation */}
            {showConfirmModal && confirmData && (
                <ConfirmWithdrawalModal
                    show={showConfirmModal}
                    onClose={() => {
                        setShowConfirmModal(false);
                        setConfirmData(null);
                    }}
                    expires_in={confirmData.expires_in}
                    trans_id={confirmData.trans_id}
                    onSuccess={handleConfirmSuccess}
                />
            )}
            
            {/* Ancien modal (gardé pour référence, peut être supprimé) */}
            {false && showNewWithdrawalModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900">Nouveau Retrait</h3>
                            <button
                                onClick={() => setShowNewWithdrawalModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-2xl transition-colors"
                            >
                                <Close />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            {/* Type de retrait */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Type de retrait
                                </label>
                                <select
                                    value={activeTab}
                                    onChange={(e) => setActiveTab(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                >
                                    {withdrawalOptions.map(option => (
                                        <option key={option.id} value={option.id}>
                                            {option.title} {option.fee !== '0%' && `(${option.fee})`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Montant */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Montant ({currency})
                                </label>
                                <input
                                    type="number"
                                    value={withdrawalData.amount}
                                    onChange={(e) => handleInputChange('amount', e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    placeholder="0.00"
                                />
                            </div>

                            {/* Type de méthode */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Méthode de retrait
                                </label>
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <button
                                        onClick={() => handleMethodTypeChange('bank')}
                                        className={`p-4 border-2 rounded-2xl transition-all duration-200 text-center ${
                                            withdrawalData.methodType === 'bank'
                                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                                        }`}
                                    >
                                        <AccountBalance className="text-2xl mb-2 mx-auto" />
                                        <div className="font-medium">Compte Bancaire</div>
                                    </button>
                                    <button
                                        onClick={() => handleMethodTypeChange('mobile')}
                                        className={`p-4 border-2 rounded-2xl transition-all duration-200 text-center ${
                                            withdrawalData.methodType === 'mobile'
                                                ? 'border-green-500 bg-green-50 text-green-700'
                                                : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                                        }`}
                                    >
                                        <Payment className="text-2xl mb-2 mx-auto" />
                                        <div className="font-medium">Mobile Money</div>
                                    </button>
                                </div>
                            </div>

                            {/* Sélection de la banque (si méthode bancaire choisie) */}
                            {withdrawalData.methodType === 'bank' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Banque
                                        </label>
                                        <select
                                            value={withdrawalData.bankProvider}
                                            onChange={(e) => handleInputChange('bankProvider', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                        >
                                            <option value="">Sélectionnez une banque</option>
                                            {bankProviders.map(bank => (
                                                <option key={bank.id} value={bank.id}>
                                                    {bank.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Numéro de compte
                                        </label>
                                        <input
                                            type="text"
                                            value={withdrawalData.accountNumber}
                                            onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                            placeholder="Entrez le numéro de compte"
                                        />
                                    </div>
                                </>
                            )}

                            {/* Sélection Mobile Money (si méthode mobile choisie) */}
                            {withdrawalData.methodType === 'mobile' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Opérateur Mobile Money
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setShowMobileMoneyModal(true)}
                                            className={`w-full px-4 py-3 border-2 rounded-2xl text-left transition-all duration-200 ${
                                                withdrawalData.mobileMoneyProvider
                                                    ? 'border-amber-500 bg-amber-50'
                                                    : 'border-gray-300 hover:border-amber-300'
                                            }`}
                                        >
                                            {withdrawalData.mobileMoneyProvider ? (
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium">
                                                        {mobileMoneyProviders.find(p => p.id === withdrawalData.mobileMoneyProvider)?.name}
                                                    </span>
                                                    <CheckCircle className="text-amber-500" />
                                                </div>
                                            ) : (
                                                <span className="text-gray-500">Sélectionnez un opérateur</span>
                                            )}
                                        </button>
                                    </div>

                                    {withdrawalData.mobileMoneyProvider && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Numéro de téléphone
                                            </label>
                                            <input
                                                type="tel"
                                                value={withdrawalData.phoneNumber}
                                                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                placeholder="+243 XXX XXX XXX"
                                            />
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Informations frais pour retrait instantané */}
                            {activeTab === 'instant' && withdrawalData.amount && (
                                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span>Montant:</span>
                                            <span>{withdrawalData.amount} {currency}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Frais (1.5%):</span>
                                            <span className="text-amber-600">-{calculateFee()} {currency}</span>
                                        </div>
                                        <div className="flex justify-between font-semibold border-t border-amber-200 pt-2">
                                            <span>Montant reçu:</span>
                                            <span className="text-green-600">{calculateTotal()} {currency}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description (optionnel)
                                </label>
                                <textarea
                                    value={withdrawalData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    rows="3"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    placeholder="Description du retrait..."
                                />
                            </div>
                        </div>

                        <div className="flex space-x-3 p-6 border-t border-gray-200">
                            <button
                                onClick={handleCloseModal}
                                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSubmitWithdrawal}
                                disabled={!withdrawalData.amount || !withdrawalData.methodType ||
                                    (withdrawalData.methodType === 'bank' && (!withdrawalData.bankProvider || !withdrawalData.accountNumber)) ||
                                    (withdrawalData.methodType === 'mobile' && (!withdrawalData.mobileMoneyProvider || !withdrawalData.phoneNumber))}
                                className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-2xl font-semibold hover:from-amber-600 hover:to-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Confirmer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Mobile Money - CORRIGÉ */}
            {showMobileMoneyModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
                    <div className="bg-white rounded-2xl w-full max-w-md">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-xl font-bold text-gray-900">Choisir Mobile Money</h3>
                            <button
                                onClick={() => setShowMobileMoneyModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-2xl transition-colors"
                            >
                                <Close />
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="space-y-3">
                                {mobileMoneyProviders.map(provider => (
                                    <button
                                        key={provider.id}
                                        type="button"
                                        onClick={() => handleMobileMoneyProviderSelect(provider)}
                                        className={`w-full flex items-center space-x-4 p-4 border-2 rounded-2xl transition-all duration-200 ${
                                            withdrawalData.mobileMoneyProvider === provider.id
                                                ? 'border-amber-500 bg-amber-50'
                                                : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                                        }`}
                                    >
                                        <div className="text-2xl">{provider.icon}</div>
                                        <div className="flex-1 text-left">
                                            <h4 className="font-semibold text-gray-900">{provider.name}</h4>
                                            <p className="text-sm text-gray-600">{provider.description}</p>
                                        </div>
                                        {withdrawalData.mobileMoneyProvider === provider.id && (
                                            <CheckCircle className="text-amber-500" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex space-x-3 p-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => setShowMobileMoneyModal(false)}
                                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors"
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Détail du retrait : même modale (reçu PDF, partage) que les transactions */}
            {selectedWithdrawal && (
                <TransactionModal
                    transaction={selectedWithdrawal}
                    onClose={() => setSelectedWithdrawal(null)}
                />
            )}
        </div>
    );
};

export default WithdrawalPage;